import type { MapViewBlockFieldsFragment } from '$lib/queries/fragments/generated/MapViewBlockFields'
import type { PinState } from '$lib/stores/block-state'
import type { BlockType } from '$lib/graphql/types'
import { BlockType as BlockTypeEnum, CustomColor } from '$lib/graphql/enums'
import { browser } from '$app/environment'
import { goto } from '$app/navigation'
import debounce from 'lodash-es/debounce'
import blockState from '../stores/block-state'
import { mapStore } from '$lib/stores/map-state'
import { urlState } from '$lib/stores/url-state'
import { isMobile } from './isMobile'
import { setDefaultBounds } from '$lib/map/map-bounds'
import { get } from 'svelte/store'

export interface MapBounds {
	north: number
	south: number
	east: number
	west: number
}

export interface MapCenter {
	lat: number
	lng: number
}

export interface MapComponentState {
	map: google.maps.Map | null
	isLoaded: boolean
	isDragging: boolean
	isMoving: boolean
	zoom: number
	bounds: MapBounds | null
	center: MapCenter | null
	isProcessingBounds: boolean
	isMobileScreen: boolean
}

export type MapAction =
	| { type: 'SET_MAP'; payload: google.maps.Map }
	| { type: 'SET_IS_MOBILE_SCREEN'; payload: boolean }
	| { type: 'SET_LOADED'; payload: boolean }
	| { type: 'SET_DRAGGING'; payload: boolean }
	| { type: 'SET_ZOOM'; payload: number }
	| { type: 'SET_MOVING'; payload: boolean }
	| { type: 'SET_BOUNDS'; payload: MapBounds }
	| { type: 'SET_CENTER'; payload: MapCenter }
	| { type: 'SET_PROCESSING_BOUNDS'; payload: boolean }
	| { type: 'RESET' }

export interface MarkerLocation {
	lat: number
	lng: number
	placeId: string
}

export type MapState =
	| 'custom'
	| 'trips'
	| 'trip'
	| 'block'
	| 'destination'
	| 'search'
	| 'requirements'
	| null
export const mapDefaults = {
	options: {
		zoomControl: false,
		mapTypeControl: false,
		scaleControl: false,
		streetViewControl: false,
		rotateControl: true,
		fullscreenControl: false,
		gestureHandling: 'greedy',
		minZoom: 2,
		mapId: 'd3d5f7175c643df4',
	},
}

export function createMap(element: HTMLElement, google: typeof window.google): google.maps.Map {
	const map = new google.maps.Map(element, mapDefaults.options)
	map.setClickableIcons(false)
	// map.mapTypes.set('roadmap', {}) // Set default roadmap style
	setDefaultBounds(map)
	return map
}

export function setMobileSettings() {
	const isMobileScreen = isMobile()
	mapStore.dispatch({ type: 'SET_IS_MOBILE_SCREEN', payload: isMobileScreen })
	return isMobileScreen
}

export const setupMapListeners = (map: google.maps.Map) => {
	let moveTimeout: number | undefined
	const listeners = new Set<google.maps.MapsEventListener>()

	const dispatch = (action: MapAction) => {
		mapStore.dispatch(action)
	}

	const setMovingState = (isMoving: boolean) => {
		if (moveTimeout) clearTimeout(moveTimeout)
		dispatch({ type: 'SET_MOVING', payload: isMoving })

		if (isMoving) {
			moveTimeout = window.setTimeout(() => {
				dispatch({ type: 'SET_MOVING', payload: false })
			}, 150)
		}
	}

	const handleZoomChange = debounce(
		() => {
			const zoom = map.getZoom()
			if (typeof zoom === 'number' && !isNaN(zoom)) {
				dispatch({ type: 'SET_ZOOM', payload: zoom })

				const currentTilt = map.getTilt()
				if (typeof currentTilt === 'number' && !isNaN(currentTilt)) {
					if (zoom < 8) {
						if (currentTilt !== 0) {
							map.setTilt(0)
						}
						handleLowZoom()
					} else {
						if (currentTilt === 0) {
							map.setTilt(47)
						}
					}
				}
			}
		},
		150,
		{ maxWait: 500 },
	)

	const setupEvents = [
		{ event: 'zoom_changed', handler: () => handleZoomChange() },
		{ event: 'dragstart', handler: () => setMovingState(true) },
		{ event: 'dragend', handler: () => setMovingState(false) },
		{ event: 'idle', handler: () => setMovingState(false) },
	]

	// Try to set up all listeners, regardless of failures
	setupEvents.forEach(({ event, handler }) => {
		try {
			const listener = map.addListener(event, () => {
				try {
					handler()
				} catch (error) {
					console.error(`Error in ${event} handler:`, error)
					dispatch({ type: 'SET_MOVING', payload: false })
				}
			})
			listeners.add(listener)
		} catch (error) {
			console.error(`Error setting up ${event} listener:`, error)
		}
	})

	return () => {
		if (moveTimeout) clearTimeout(moveTimeout)

		// Clean up all listeners, continuing even if some fail
		const errors: Error[] = []
		listeners.forEach((listener) => {
			try {
				google.maps.event.removeListener(listener)
			} catch (error) {
				errors.push(error as Error)
				console.error('Error removing listener:', error)
			}
		})
		listeners.clear()

		handleZoomChange.cancel()
		dispatch({ type: 'SET_MOVING', payload: false })
	}
}

/**
 * Converts our internal MapBounds format to google.maps.LatLngBounds
 */
export function toLatLngBounds(bounds: MapBounds): google.maps.LatLngBounds {
	return new google.maps.LatLngBounds(
		{ lat: bounds.south, lng: bounds.west }, // SW corner
		{ lat: bounds.north, lng: bounds.east }, // NE corner
	)
}

/**
 * Converts google.maps.LatLngBounds to our internal MapBounds format
 */
export function fromLatLngBounds(bounds: google.maps.LatLngBounds): MapBounds {
	const boundsObj = bounds.toJSON()
	return {
		north: boundsObj.north,
		south: boundsObj.south,
		east: boundsObj.east,
		west: boundsObj.west,
	}
}

/**
 * Checks if bounds are valid (not null and have valid coordinates)
 */
export function isValidBounds(bounds: MapBounds | null): bounds is MapBounds {
	if (!bounds) return false

	return (
		typeof bounds.north === 'number' &&
		typeof bounds.south === 'number' &&
		typeof bounds.east === 'number' &&
		typeof bounds.west === 'number' &&
		!isNaN(bounds.north) &&
		!isNaN(bounds.south) &&
		!isNaN(bounds.east) &&
		!isNaN(bounds.west)
	)
}

function handleLowZoom() {
	const { parentBlockId, panel } = get(urlState)
	if (parentBlockId && !panel && browser) {
		const url = new URL(window.location.href)

		if (url.searchParams.has('parent')) {
			url.searchParams.delete('parent')
			blockState.setRecenterMap(false)
			goto(url, { keepFocus: false })
		}
	}
}

export function setupClickListener(
	map: google.maps.Map,
	onClick: (event: google.maps.MapMouseEvent) => void,
): google.maps.MapsEventListener {
	return map.addListener('click', onClick)
}

export function filterSearchResultsByBounds(
	results: PinState[],
	bounds: google.maps.LatLngBounds | null,
): PinState[] {
	if (!bounds || !results?.length) return results

	return results.filter(
		(res) =>
			// Check if lat/lng are numbers (including 0) instead of just truthy
			typeof res?.lat === 'number' &&
			typeof res?.lng === 'number' &&
			bounds.contains({
				lat: res.lat,
				lng: res.lng,
			}),
	)
}

export function convertRequirementBlocksToPinState(
	blocks: MapViewBlockFieldsFragment[],
): PinState[] {
	return blocks.map((block) => ({
		id: block.id,
		lat: block.point.lat,
		lng: block.point.lng,
		blockType: block.blockType,
		color: CustomColor.Pink,
		icon: block.icon,
		isRequirementsMarker: true,
		isSearchMarker: false,
		isOrigin: false,
	}))
}

export function filterBlocksByBounds(
	blocks: MapViewBlockFieldsFragment[],
	bounds: google.maps.LatLngBounds | null,
	zoom: number,
): MapViewBlockFieldsFragment[] {
	if (!blocks?.length) return []

	if (!bounds) {
		return blocks.filter(
			(block) =>
				typeof block?.point?.lat === 'number' &&
				typeof block?.point?.lng === 'number' &&
				isBlockVisibleAtZoom(block?.blockType, zoom),
		)
	}

	return blocks.filter((block) => {
		if (typeof block?.point?.lat !== 'number' || typeof block?.point?.lng !== 'number') return false
		if (!isBlockVisibleAtZoom(block?.blockType, zoom)) return false

		return bounds.contains({ lat: block.point.lat, lng: block.point.lng })
	})
}

export function filterPolygonsByBounds(
	blocks: MapViewBlockFieldsFragment[],
	bounds: MapBounds | null,
	zoom: number,
): MapViewBlockFieldsFragment[] {
	if (!blocks?.length) return []

	if (!bounds) {
		return blocks.filter(
			(block) =>
				block?.customRegion?.markers?.length > 0 && isBlockVisibleAtZoom(block?.blockType, zoom),
		)
	}

	return blocks.filter((block) => {
		if (!block.customRegion?.markers?.length) return false
		if (!isBlockVisibleAtZoom(block?.blockType, zoom)) return false

		const googleBounds = toLatLngBounds(bounds)

		// Check if any point of the polygon is within bounds
		return block.customRegion.markers.some((point) =>
			googleBounds.contains({ lat: point.lat, lng: point.lng }),
		)
	})
}

export function isBlockVisibleAtZoom(blockType: BlockType, zoom: number): boolean {
	if (zoom >= 8) {
		return blockType !== BlockTypeEnum.Destination
	} else {
		return blockType === BlockTypeEnum.Destination
	}
}

export function isBlockHighlighted(
	block: MapViewBlockFieldsFragment,
	selectedBlockId: string | null,
	parentBlockId: string | null,
	expandedBlockId: string | null,
	isActive: boolean,
	isHighlights: boolean,
): boolean {
	const isParentActive =
		(selectedBlockId &&
			!expandedBlockId &&
			(block?.parentDayId === selectedBlockId ||
				block?.parentId === selectedBlockId ||
				block?.parentListId === selectedBlockId ||
				block?.parentDestination?.id === selectedBlockId)) ||
		(isHighlights &&
			parentBlockId &&
			(block?.parentDayId === parentBlockId ||
				block?.parentId === parentBlockId ||
				block?.parentListId === parentBlockId ||
				block?.parentDestination?.id === parentBlockId)) ||
		(expandedBlockId &&
			(block?.parentDayId === expandedBlockId ||
				block?.parentId === expandedBlockId ||
				block?.parentListId === expandedBlockId ||
				block?.parentDestination?.id === expandedBlockId))

	return isActive === true || isParentActive === true
}
